import * as THREE from 'three';
import { addAtmosphere } from '../scene/atmosphere';
import { createWeather } from '../scene/Weather';
import { terrainHeight } from '../world/terrain';
import { BIOME_PREVIEW_ORIGINS, type BiomeName } from '../world/biomes';
import { World } from '../world/World';
import { Skier } from './Skier';
import { TouchSteering } from './TouchSteering';
import { createSkiRig } from './createSkiRig';

export interface StartGameOptions {
  readonly screenshotMode?: boolean;
  readonly screenshotBiome?: BiomeName;
}

interface LiveBiomeCapture {
  readonly x: number;
  readonly z: number;
  readonly heading: number;
}

/** Stable compositions for auditing the actual streamed game world. */
export const LIVE_BIOME_CAPTURES: Record<BiomeName, LiveBiomeCapture> = {
  lanternwood: {
    ...BIOME_PREVIEW_ORIGINS.lanternwood,
    heading: 0,
  },
  // The field-guide anchor sits immediately downhill of its clearest stable
  // crystal corridor, so the live study begins slightly uphill and across the
  // slope. The angled heading layers its two nearest fans in portrait.
  blueIce: {
    x: BIOME_PREVIEW_ORIGINS.blueIce.x - 15,
    z: BIOME_PREVIEW_ORIGINS.blueIce.z - 37,
    heading: -16 * Math.PI / 180,
  },
  // The gallery anchor faces an open ridge. This nearby cross-slope view puts
  // two fences, krummholz, and cairns into even portrait's narrow forward cone.
  timberline: {
    x: 600,
    z: -2_918,
    heading: -143 * Math.PI / 180,
  },
};

/** Starts the play view and returns everything needed to tear it down for HMR. */
export function startGame(mount: HTMLElement, options: StartGameOptions = {}): () => void {
  const screenshotMode = options.screenshotMode ?? false;
  const screenshotCapture = options.screenshotBiome
    ? LIVE_BIOME_CAPTURES[options.screenshotBiome]
    : undefined;
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
  renderer.outputColorSpace = THREE.SRGBColorSpace;
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.12;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  renderer.domElement.classList.toggle('screenshot-canvas', screenshotMode);
  mount.append(renderer.domElement);

  const scene = new THREE.Scene();
  const atmosphere = addAtmosphere(scene);
  const weather = createWeather(scene);

  const camera = new THREE.PerspectiveCamera(72, 1, 0.045, 440);
  const skiRig = createSkiRig();
  camera.add(skiRig);
  scene.add(camera);

  const world = new World(scene);
  const skier = new Skier(camera, skiRig, world);
  const steering = screenshotMode ? null : new TouchSteering(renderer.domElement);
  if (screenshotCapture) {
    skier.x = screenshotCapture.x;
    skier.z = screenshotCapture.z;
    skier.y = terrainHeight(skier.x, skier.z);
    skier.heading = screenshotCapture.heading;
  }
  skier.update(0, 0);
  world.update(skier.x, skier.z, terrainHeight(skier.x, skier.z));

  let animationFrame = 0;
  let previousTime = performance.now();
  let accumulator = 0;
  const fixedStep = 1 / 90;

  function animate(now: number): void {
    animationFrame = requestAnimationFrame(animate);
    const frameDelta = Math.min((now - previousTime) / 1000, 0.08);
    previousTime = now;
    accumulator += frameDelta;
    steering?.update(frameDelta);

    while (!screenshotMode && steering && accumulator >= fixedStep) {
      skier.update(fixedStep, steering.value);
      accumulator -= fixedStep;
    }

    const groundHeight = terrainHeight(skier.x, skier.z);
    world.update(skier.x, skier.z, groundHeight);
    atmosphere.update(skier.x, skier.z, frameDelta, screenshotMode);
    weather.update(skier.x, skier.z, frameDelta, screenshotMode);
    renderer.render(scene, camera);
  }

  function resize(): void {
    const width = mount.clientWidth;
    const height = mount.clientHeight;
    if (width === 0 || height === 0) return;

    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.6));
    renderer.setSize(width, height, false);
  }

  function resetClock(): void {
    previousTime = performance.now();
    accumulator = 0;
  }

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(mount);
  document.addEventListener('visibilitychange', resetClock);
  resize();
  animationFrame = requestAnimationFrame(animate);

  return () => {
    cancelAnimationFrame(animationFrame);
    resizeObserver.disconnect();
    document.removeEventListener('visibilitychange', resetClock);
    steering?.dispose();
    weather.dispose();
    atmosphere.dispose();
    world.dispose();
    renderer.dispose();
    renderer.domElement.remove();
  };
}
